/**
 * DesmosGraph Component
 * Renders a Desmos calculator graph with optional variable substitution
 * Ported from reference_legacy/quebon2.0/quebon-tv-web/src/components/Quiz/components/Desmos.js
 */

import { useEffect, useRef, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '../../../lib/utils'

// Desmos API types
interface DesmosExpression {
  type: string
  id: string
  latex?: string
  isChanged?: boolean
  slider?: {
    min?: number
    max?: number
  }
}

interface DesmosState {
  version?: number
  randomSeed?: string
  graph?: {
    viewport?: {
      xmin: number
      xmax: number
      ymin: number
      ymax: number
    }
    showGrid?: boolean
    showXAxis?: boolean
    showYAxis?: boolean
  }
  expressions?: {
    list?: DesmosExpression[]
  } | DesmosExpression[]
  _options?: {
    viewport?: 'landscape' | 'portrait'
    [key: string]: unknown
  }
}

interface DesmosCalculatorOptions {
  keypad?: boolean
  graphpaper?: boolean
  expressions?: boolean
  settingsMenu?: boolean
  zoomButtons?: boolean
  expressionsTopbar?: boolean
  pointsOfInterest?: boolean
  trace?: boolean
  border?: boolean
  lockViewport?: boolean
  showGrid?: boolean
  showXAxis?: boolean
  showYAxis?: boolean
  fontSize?: number
}

// Declare global Desmos API
declare global {
  interface Window {
    Desmos?: {
      GraphingCalculator: (
        element: HTMLElement,
        options?: DesmosCalculatorOptions
      ) => {
        setState: (state: DesmosState) => void
        getState: () => DesmosState
        getExpressions: () => DesmosExpression[]
        observeEvent: (event: string, callback: () => void) => void
        observe: (property: string, callback: () => void) => void
        expressionAnalysis: Record<string, { evaluation?: { value: number } }>
        destroy: () => void
      }
    }
  }
}

interface DesmosGraphProps {
  /** Desmos state as JSON string */
  state: string
  /** Variables to substitute in expressions (e.g., { _a: 5, _b: 3 }) */
  vars?: Record<string, unknown>
  /** Callback when expression values change */
  onChange?: (data: Record<string, string>) => void
  /** Additional CSS classes */
  className?: string
  /** Whether to show expression panel (for debugging/admin) */
  showExpressions?: boolean
}

const DESMOS_SCRIPT_ID = 'desmos-api-script'
const DESMOS_API_URL =
  'https://www.desmos.com/api/v1.5/calculator.js?apiKey=dcb31709b452b1cf9dc26972add0fda6'

/**
 * Load Desmos API script dynamically
 */
function loadDesmosScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    // Already loaded
    if (window.Desmos) {
      resolve()
      return
    }

    // Check if script is already being loaded
    const existingScript = document.getElementById(DESMOS_SCRIPT_ID)
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve())
      existingScript.addEventListener('error', () =>
        reject(new Error('Failed to load Desmos API'))
      )
      return
    }

    // Create and load script
    const script = document.createElement('script')
    script.id = DESMOS_SCRIPT_ID
    script.src = DESMOS_API_URL
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Desmos API'))
    document.head.appendChild(script)
  })
}

/**
 * Process variables and substitute them into Desmos expressions
 */
function processVarsIntoExpressions(
  expressionList: DesmosExpression[],
  vars?: Record<string, unknown>
): DesmosExpression[] {
  if (!vars) return expressionList

  // Build regex object from vars (e.g., _a -> a, _ab -> a_{b})
  const regexObj: Record<string, string> = {}
  Object.keys(vars).forEach((key) => {
    if (key.startsWith('_')) {
      let varValue = vars[key]
      let varKey = key.replace('_', '')

      // Convert array to Desmos list format
      if (Array.isArray(varValue)) {
        varValue = `[${varValue.join(',')}]`
      }

      // Handle subscript notation for multi-character keys (e.g., ab -> a_{b})
      if (varKey.length > 1) {
        varKey = `${varKey.slice(0, 1)}_{${varKey.slice(1)}}`
      }

      regexObj[varKey] = String(varValue)
    }
  })

  const usedKeys: string[] = []
  const newList: DesmosExpression[] = []

  // Substitute variables in existing expressions
  expressionList.forEach((item) => {
    const newItem = { ...item }
    if (newItem.latex && newItem.latex.includes('=')) {
      let latexKey = newItem.latex.split('=')[0].trim()
      latexKey = latexKey.replace('\\', '').trim()

      const varValue = regexObj[latexKey]
      if (varValue !== undefined) {
        newItem.latex = `${latexKey}=${varValue}`
        newItem.isChanged = true
        usedKeys.push(latexKey)
      }
    }
    newList.push(newItem)
  })

  // Add unused variables as new expressions
  Object.keys(regexObj)
    .filter((k) => !usedKeys.includes(k))
    .forEach((key, index) => {
      newList.push({
        type: 'expression',
        id: `extra_${index}`,
        latex: `${key}=${regexObj[key]}`,
      })
    })

  return newList
}

/**
 * DesmosGraph component for rendering Desmos calculator graphs
 */
export function DesmosGraph({
  state,
  vars,
  onChange,
  className,
  showExpressions = false,
}: DesmosGraphProps) {
  const { t } = useTranslation('quiz')
  const containerRef = useRef<HTMLDivElement>(null)
  const calculatorRef = useRef<ReturnType<
    NonNullable<typeof window.Desmos>['GraphingCalculator']
  > | null>(null)
  const [viewport, setViewport] = useState<'landscape' | 'portrait'>('landscape')
  const [isLoaded, setIsLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleChange = useCallback(
    (
      calculator: ReturnType<
        NonNullable<typeof window.Desmos>['GraphingCalculator']
      >
    ) => {
      if (!onChange) return

      const expressions = calculator.getExpressions()
      const data: Record<string, string> = {}

      const regex = /^([a-zA-Z0-9_{}]+)[\s\\]*=[\s\\]*([-+]?[0-9.]+)$/

      expressions.forEach((e) => {
        const latex = e.latex
        if (!latex) return

        const match = regex.exec(latex)
        if (match) {
          data[match[1].replace(/[_{}]/g, '')] = match[2]
        } else {
          const analysis = calculator.expressionAnalysis[e.id]
          if (analysis?.evaluation) {
            const varName = latex.split('=')[0].replace(/[_{}\\]/g, '').trim()
            data[varName] = String(analysis.evaluation.value)
          }
        }
      })

      onChange(data)
    },
    [onChange]
  )

  const initCalculator = useCallback(async () => {
    if (!containerRef.current || !state) return

    try {
      await loadDesmosScript()

      if (!window.Desmos) {
        setError('Desmos API not available')
        return
      }

      // Clean up previous calculator
      if (calculatorRef.current) {
        calculatorRef.current.destroy()
        calculatorRef.current = null
      }

      // Clear container
      containerRef.current.innerHTML = ''

      // Parse state
      let parsedState: DesmosState
      try {
        parsedState = JSON.parse(state)
      } catch {
        setError('Invalid Desmos state JSON')
        return
      }

      // Extract expression list
      let expressionList: DesmosExpression[] = []
      if (parsedState.expressions) {
        if (Array.isArray(parsedState.expressions)) {
          expressionList = parsedState.expressions
        } else if (parsedState.expressions.list) {
          expressionList = parsedState.expressions.list
        }
      }

      // Clear expressions from state (will be added after processing)
      parsedState.expressions = { list: [] }

      // Build calculator options
      const options: DesmosCalculatorOptions = {
        keypad: false,
        graphpaper: true,
        expressions: showExpressions,
        settingsMenu: false,
        zoomButtons: false,
        expressionsTopbar: false,
        pointsOfInterest: false,
        trace: false,
        border: false,
        lockViewport: false,
        showGrid: true,
        showXAxis: true,
        showYAxis: true,
        fontSize: 24,
      }

      // Apply custom options from state
      if (parsedState._options) {
        Object.assign(options, parsedState._options)

        if (parsedState._options.viewport) {
          setViewport(parsedState._options.viewport)
        }
      }

      // Create calculator
      const calculator = window.Desmos.GraphingCalculator(
        containerRef.current,
        options
      )
      calculatorRef.current = calculator

      // Process variables and set state
      const processedList = processVarsIntoExpressions(expressionList, vars)
      parsedState.expressions = { list: processedList }
      calculator.setState(parsedState)

      // Set up change observers
      if (onChange) {
        handleChange(calculator)

        calculator.observeEvent('change', () => {
          handleChange(calculator)
        })

        calculator.observe('expressionAnalysis', () => {
          handleChange(calculator)
        })
      }

      setIsLoaded(true)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initialize Desmos')
    }
  }, [state, vars, onChange, showExpressions, handleChange])

  useEffect(() => {
    initCalculator()

    return () => {
      if (calculatorRef.current) {
        calculatorRef.current.destroy()
        calculatorRef.current = null
      }
    }
  }, [initCalculator])

  if (error) {
    return (
      <div className={cn('flex items-center justify-center p-4 text-red-500', className)}>
        <span>{t('shared.cannotLoadGraph', { error })}</span>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        'desmos-graph',
        viewport === 'portrait' ? 'aspect-3/4' : 'aspect-4/3',
        'w-full max-w-[600px] min-h-[300px]',
        !isLoaded && 'bg-gray-100 animate-pulse',
        className
      )}
    />
  )
}
